import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import { userManagementApi } from '../../api/userManagement';
import { useToast } from '../../hooks/useToast';
import { getUser } from '../../api/auth';

const normalizeEstado = (estado) => {
  if (!estado) return 'inactive';
  const s = String(estado).toLowerCase();
  if (s === 'activo' || s === 'active') return 'active';
  return 'inactive';
};

const mockPermissions = {
  "usuarios": ["ver", "crear", "editar", "eliminar"],
  "roles": ["ver", "crear", "editar", "eliminar"],
  "Chats": ["ver", "crear", "editar", "cancelar"],
  "Estrategias": ["ver", "crear", "editar", "cancelar"],
  "Metricas": ["ver", "crear", "editar", "cancelar"],
  "Comprobantes": ["ver", "crear", "editar", "cancelar"],
};

const formatModuloLabel = (modulo) => {
  const cleaned = String(modulo).replace(/_/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

export const UserManagementSettings = () => {
  const queryClient = useQueryClient();
  const { showToast, ToastComponent } = useToast();

  const [tab, setTab] = useState(0);

  const [userFilters, setUserFilters] = useState({
    search: '',
    roleId: '',
    branchId: '',
    status: '',
  });

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    nombre: '',
    usuario: '',
    idrol: '',
    idsucursal: '',
    password: '',
  });
  const [resetPasswordValue, setResetPasswordValue] = useState('');

  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    nombre: '',
    descripcion: '',
  });
  const [selectedPermissions, setSelectedPermissions] = useState({});

  const canEditRoles = true; // UI-only flag. Backend permissions enforce access.

  const { data: sucursalesRaw, isLoading: sucursalesLoading } = useQuery({
    queryKey: ['sucursales'],
    queryFn: () => userManagementApi.getSucursales(),
  });

  const { data: rolesRaw, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => userManagementApi.getRoles(),
  });

  const { data: usuariosRaw, isLoading: usuariosLoading } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => userManagementApi.getUsuarios(),
  });

  const {
    data: permisosRaw,
    isLoading: permisosLoading,
    isError: permisosError,
    error: permisosErrorObj,
  } = useQuery({
    queryKey: ['permisos'],
    queryFn: () => Promise.resolve(mockPermissions),
    initialData: mockPermissions,
    enabled: false, // Use static mock permissions (same structure as bankivo Configuracion)
  });

  const sucursales = useMemo(() => {
    return Array.isArray(sucursalesRaw) ? sucursalesRaw : [];
  }, [sucursalesRaw]);

  const roles = useMemo(() => {
    if (!Array.isArray(rolesRaw)) return [];
    return rolesRaw.map((role) => ({
      id: role.value,
      name: role.label,
      description: role.descripcion,
      permissions:
        typeof role.permisos === 'string'
          ? (() => {
              try {
                return JSON.parse(role.permisos);
              } catch {
                return {};
              }
            })()
          : role.permisos || {},
      users: role.num_usuarios ?? 0,
    }));
  }, [rolesRaw]);

  const usuarios = useMemo(() => {
    if (!Array.isArray(usuariosRaw)) return [];
    return usuariosRaw.map((u) => ({
      id: u.value,
      nombre: u.description,
      usuario: u.label,
      idrol: u.idrole,
      role: u.role,
      idsucursal: u.idsucursal,
      branch: u.sucursal,
      status: normalizeEstado(u.estado),
    }));
  }, [usuariosRaw]);

  // Same structure used in `bankivo/src/modules/configuracion/index.js`
  // (module -> allowed actions).
  const permisosMap = mockPermissions;

  const moduleGroups = useMemo(() => {
    return [
      {
        categoria: 'Permisos',
        modulos: Object.keys(permisosMap).map((key) => ({
          key,
          label: formatModuloLabel(key),
        })),
      },
    ];
  }, []);

  const permissionsSkeleton = useMemo(() => {
    const skeleton = {};
    Object.keys(permisosMap).forEach((key) => {
      skeleton[key] = [];
    });
    return skeleton;
  }, []);

  const filteredUsers = useMemo(() => {
    const { search, roleId, branchId, status } = userFilters;
    const s = search.trim().toLowerCase();

    return usuarios.filter((u) => {
      const matchesSearch = !s || `${u.nombre ?? ''}`.toLowerCase().includes(s) || `${u.usuario ?? ''}`.toLowerCase().includes(s);
      const matchesRole = !roleId || String(u.idrol) === String(roleId);
      const matchesBranch = !branchId || String(u.idsucursal) === String(branchId);
      const matchesStatus = !status || u.status === status;
      return matchesSearch && matchesRole && matchesBranch && matchesStatus;
    });
  }, [usuarios, userFilters]);

  const handleTabChange = (_, next) => setTab(next);

  const openCreateUser = () => {
    setEditingUser(null);
    setUserDialogOpen(true);
    setUserForm({
      nombre: '',
      usuario: '',
      idrol: roles.find((r) => String(r.name).toLowerCase().includes('administrador'))?.id || roles[0]?.id || '',
      idsucursal: '',
      password: '',
    });
    setResetPasswordValue('');
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserDialogOpen(true);
    setUserForm({
      nombre: user.nombre || '',
      usuario: user.usuario || '',
      idrol: user.idrol || '',
      idsucursal: user.idsucursal || '',
      password: '',
    });
    setResetPasswordValue('');
  };

  const closeUserDialog = () => {
    setUserDialogOpen(false);
    setEditingUser(null);
    setUserForm({ nombre: '', usuario: '', idrol: '', idsucursal: '', password: '' });
    setResetPasswordValue('');
  };

  const isAdminRole = (roleId) => {
    const role = roles.find((r) => String(r.id) === String(roleId));
    return role?.name && String(role.name).toLowerCase().includes('administrador');
  };

  const handleSaveUser = async () => {
    if (!userForm.nombre.trim() || !userForm.usuario.trim() || !userForm.idrol) {
      showToast('Completa nombre, usuario y rol', 'error');
      return;
    }

    if (!editingUser && !userForm.password.trim()) {
      showToast('Completa la contraseña', 'error');
      return;
    }

    if (!isAdminRole(userForm.idrol) && (!userForm.idsucursal || !String(userForm.idsucursal).trim())) {
      showToast('Selecciona una sucursal', 'error');
      return;
    }

    try {
      if (editingUser) {
        const requiredPerm = 'usuarios/editar';
        await userManagementApi.updateUsuario({
          id: editingUser.id,
          nombre: userForm.nombre,
          usuario: userForm.usuario,
          idrol: userForm.idrol,
          idsucursal: isAdminRole(userForm.idrol) ? null : userForm.idsucursal,
        });

        if (resetPasswordValue.trim()) {
          await userManagementApi.resetPassword({ id: editingUser.id, password: resetPasswordValue.trim() });
        }

        showToast('Usuario actualizado', 'success');
      } else {
        const requiredPerm = 'usuarios/crear';
        // Backend expects id. Use user.id if provided, otherwise create a UUID-like id.
        const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `user_${Date.now()}`;
        await userManagementApi.createUsuario({
          id,
          nombre: userForm.nombre,
          usuario: userForm.usuario,
          idrol: userForm.idrol,
          idsucursal: isAdminRole(userForm.idrol) ? null : userForm.idsucursal,
          password: userForm.password,
        });
        showToast('Usuario creado', 'success');
      }

      closeUserDialog();
      await queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    } catch (e) {
      const requiredPerm = editingUser ? 'usuarios/editar' : 'usuarios/crear';
      if ((e?.message || '').includes('Acceso denegado')) {
        showToast(`Acceso denegado: necesitas permiso ${requiredPerm}`, 'error');
      } else {
        showToast(e?.message || 'Error al guardar usuario', 'error');
      }
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    try {
      await userManagementApi.deleteUsuario(user.id);
      showToast('Usuario eliminado', 'success');
      await queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    } catch (e) {
      if ((e?.message || '').includes('Acceso denegado')) {
        showToast('Acceso denegado: necesitas permiso usuarios/eliminar', 'error');
      } else {
        showToast(e?.message || 'Error al eliminar usuario', 'error');
      }
    }
  };

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleDialogOpen(true);
    setRoleForm({ nombre: '', descripcion: '' });
    // Initialize with all modules so checkboxes render immediately.
    setSelectedPermissions(permissionsSkeleton);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleDialogOpen(true);
    setRoleForm({
      nombre: role.name || '',
      descripcion: role.description || '',
    });
    // Merge role permissions into skeleton so missing modules still appear.
    setSelectedPermissions({
      ...permissionsSkeleton,
      ...(role.permissions || {}),
    });
  };

  const closeRoleDialog = () => {
    setRoleDialogOpen(false);
    setEditingRole(null);
    setRoleForm({ nombre: '', descripcion: '' });
    setSelectedPermissions({});
  };

  useEffect(() => {
    // If permissions load after the dialog opens, keep skeleton + selections in sync
    // without wiping the user's selected actions.
    if (!roleDialogOpen) return;
    if (permisosLoading) return;

    setSelectedPermissions((prev) => ({
      ...permissionsSkeleton,
      ...(prev || {}),
    }));
  }, [roleDialogOpen, permisosLoading, permissionsSkeleton]);

  const togglePermission = (modulo, action, checked) => {
    setSelectedPermissions((prev) => {
      const current = new Set(prev?.[modulo] || []);
      if (checked) current.add(action);
      else current.delete(action);
      const nextArr = Array.from(current).sort();
      return { ...prev, [modulo]: nextArr };
    });
  };

  const selectAllPermissionsGlobal = (checked) => {
    if (!checked) {
      setSelectedPermissions(permissionsSkeleton);
      return;
    }
    // Only select actions we currently know (from /api/permisos).
    const next = {};
    moduleGroups.forEach((group) => {
      (group.modulos || []).forEach((m) => {
        const acciones = permisosMap[m.key] || [];
        next[m.key] = acciones.map(String);
      });
    });
    setSelectedPermissions(next);
  };

  const handleSaveRole = async () => {
    if (!roleForm.nombre.trim()) {
      showToast('Completa el nombre del rol', 'error');
      return;
    }
    const cleaned = Object.fromEntries(Object.entries(selectedPermissions).filter(([_, actions]) => Array.isArray(actions) && actions.length > 0));

    try {
      if (editingRole) {
        const requiredPerm = 'roles/editar';
        await userManagementApi.updateRol(editingRole.id, {
          nombre: roleForm.nombre,
          descripcion: roleForm.descripcion,
          permisos: cleaned,
        });
        showToast('Rol actualizado', 'success');
      } else {
        const requiredPerm = 'roles/crear';
        await userManagementApi.createRol({
          nombre: roleForm.nombre,
          descripcion: roleForm.descripcion,
          permisos: cleaned,
        });
        showToast('Rol creado', 'success');
      }

      closeRoleDialog();
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
      await queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    } catch (e) {
      const requiredPerm = editingRole ? 'roles/editar' : 'roles/crear';
      if ((e?.message || '').includes('Acceso denegado')) {
        showToast(`Acceso denegado: necesitas permiso ${requiredPerm}`, 'error');
      } else {
        showToast(e?.message || 'Error al guardar rol', 'error');
      }
    }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm('¿Eliminar este rol?')) return;
    try {
      await userManagementApi.deleteRol(role.id);
      showToast('Rol eliminado', 'success');
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
      await queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    } catch (e) {
      if ((e?.message || '').includes('Acceso denegado')) {
        showToast('Acceso denegado: necesitas permiso roles/eliminar', 'error');
      } else {
        showToast(e?.message || 'Error al eliminar rol', 'error');
      }
    }
  };

  if (sucursalesLoading || rolesLoading || usuariosLoading) {
    return (
      <Box sx={{ p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const userDialogTitle = editingUser ? 'Editar Usuario' : 'Nuevo Usuario';
  const roleDialogTitle = editingRole ? 'Editar Rol' : 'Nuevo Rol';

  return (
    <Box sx={{ p: 6, width: '100%', maxWidth: { sm: '100%', md: '1800px' } }}>
      <ToastComponent />
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SettingsIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 650, mb: 0.5 }}>
              Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestión de usuarios y roles
            </Typography>
          </Box>
        </Box>
      </Box>

      <Paper sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 650 },
          }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label="Usuarios" />
          <Tab icon={<AdminPanelSettingsIcon />} iconPosition="start" label="Roles" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontWeight: 650 }}>
                Usuarios
              </Typography>
              <Chip label={`${filteredUsers.length} de ${usuarios.length}`} color="primary" />
            </Box>

            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateUser} sx={{ borderRadius: 1, fontWeight: 650, textTransform: 'none' }}>
              Nuevo Usuario
            </Button>
          </Box>

          <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar por nombre o usuario..."
                  value={userFilters.search}
                  onChange={(e) => setUserFilters((prev) => ({ ...prev, search: e.target.value }))}
                  sx={{ backgroundColor: '#F8F9FA', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Select
                    value={userFilters.roleId}
                    onChange={(e) => setUserFilters((prev) => ({ ...prev, roleId: e.target.value }))}
                    sx={{ backgroundColor: '#F8F9FA', borderRadius: 1 }}
                  >
                    <MenuItem value="">Todos los roles</MenuItem>
                    {roles.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Select
                    value={userFilters.branchId}
                    onChange={(e) => setUserFilters((prev) => ({ ...prev, branchId: e.target.value }))}
                    sx={{ backgroundColor: '#F8F9FA', borderRadius: 1 }}
                  >
                    <MenuItem value="">Todas las sucursales</MenuItem>
                    {sucursales.map((b) => (
                      <MenuItem key={b.value} value={b.value}>
                        {b.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <Select
                    value={userFilters.status}
                    onChange={(e) => setUserFilters((prev) => ({ ...prev, status: e.target.value }))}
                    sx={{ backgroundColor: '#F8F9FA', borderRadius: 1 }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="active">Activo</MenuItem>
                    <MenuItem value="inactive">Inactivo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 750 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 750 }}>Usuario</TableCell>
                  <TableCell sx={{ fontWeight: 750 }}>Rol</TableCell>
                  <TableCell sx={{ fontWeight: 750 }}>Sucursal</TableCell>
                  <TableCell sx={{ fontWeight: 750 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 750, textAlign: 'right' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      No hay usuarios con esos filtros
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" sx={{ fontWeight: 650 }}>
                          {u.nombre}
                        </Typography>
                      </TableCell>
                      <TableCell>{u.usuario}</TableCell>
                      <TableCell>{u.role || '-'}</TableCell>
                      <TableCell>{u.branch || '-'}</TableCell>
                      <TableCell>
                        <Chip label={u.status === 'active' ? 'Activo' : 'Inactivo'} color={u.status === 'active' ? 'success' : 'error'} size="small" />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        <IconButton size="small" onClick={() => openEditUser(u)} sx={{ color: 'primary.main' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteUser(u)} sx={{ color: 'error.main' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontWeight: 650 }}>
                Roles
              </Typography>
              <Chip label={`${roles.length} roles`} color="primary" />
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateRole}
              disabled={!canEditRoles || permisosLoading}
              sx={{ borderRadius: 1, fontWeight: 650, textTransform: 'none' }}
            >
              Nuevo Rol
            </Button>
          </Box>

          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 750 }}>Rol</TableCell>
                    <TableCell sx={{ fontWeight: 750 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 750 }}>Permisos (modulos)</TableCell>
                    <TableCell sx={{ fontWeight: 750 }}>Usuarios</TableCell>
                    <TableCell sx={{ fontWeight: 750, textAlign: 'right' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                        No hay roles
                      </TableCell>
                    </TableRow>
                  ) : (
                    roles.map((r) => {
                      const permisoModulesCount = Object.keys(r.permissions || {}).length;
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ fontWeight: 650 }}>{r.name}</TableCell>
                          <TableCell>{r.description || '-'}</TableCell>
                          <TableCell>{permisoModulesCount}</TableCell>
                          <TableCell>{r.users}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            <IconButton size="small" onClick={() => openEditRole(r)} sx={{ color: 'primary.main' }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteRole(r)} sx={{ color: 'error.main' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* User dialog */}
      <Dialog
        open={userDialogOpen}
        onClose={closeUserDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 1, borderRadius: 1.5, backgroundColor: 'primary.main', display: 'flex', color: 'white' }}>
              <PersonIcon />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 650 }}>
              {userDialogTitle}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Nombre completo"
                value={userForm.nombre}
                onChange={(e) => setUserForm((prev) => ({ ...prev, nombre: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Usuario"
                value={userForm.usuario}
                onChange={(e) => setUserForm((prev) => ({ ...prev, usuario: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <Select
                  value={userForm.idrol}
                  onChange={(e) => setUserForm((prev) => ({ ...prev, idrol: e.target.value }))}
                  displayEmpty
                  sx={{ backgroundColor: '#F8F9FA', borderRadius: 1 }}
                >
                  {roles.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <Select
                  value={userForm.idsucursal || ''}
                  onChange={(e) => setUserForm((prev) => ({ ...prev, idsucursal: e.target.value }))}
                  displayEmpty
                  disabled={isAdminRole(userForm.idrol)}
                  sx={{ backgroundColor: '#F8F9FA', borderRadius: 1 }}
                >
                  <MenuItem value="">
                    {isAdminRole(userForm.idrol) ? 'Administrador sin sucursal' : 'Selecciona sucursal'}
                  </MenuItem>
                  {sucursales.map((b) => (
                    <MenuItem key={b.value} value={b.value}>
                      {b.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {!editingUser && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Contraseña"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm((prev) => ({ ...prev, password: e.target.value }))}
                />
              </Grid>
            )}

            {editingUser && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nueva contraseña (opcional)"
                  type="password"
                  value={resetPasswordValue}
                  onChange={(e) => setResetPasswordValue(e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={closeUserDialog} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveUser} sx={{ textTransform: 'none', fontWeight: 650 }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={closeRoleDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 1, borderRadius: 1.5, backgroundColor: 'primary.main', display: 'flex', color: 'white' }}>
              <AdminPanelSettingsIcon />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 650 }}>
              {roleDialogTitle}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Nombre del rol"
                value={roleForm.nombre}
                onChange={(e) => setRoleForm((prev) => ({ ...prev, nombre: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Descripción"
                value={roleForm.descripcion}
                onChange={(e) => setRoleForm((prev) => ({ ...prev, descripcion: e.target.value }))}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => selectAllPermissionsGlobal(true)}
              sx={{ textTransform: 'none', fontWeight: 650 }}
            >
              Seleccionar todo
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => selectAllPermissionsGlobal(false)}
              sx={{ textTransform: 'none', fontWeight: 650 }}
            >
              Deseleccionar todo
            </Button>
          </Box>

          <Box sx={{ maxHeight: 520, overflow: 'auto', pr: 1 }}>
            {permisosError ? (
              <Typography color="error" sx={{ py: 1 }}>
                Error cargando permisos: {permisosErrorObj?.message || 'Error desconocido'}
              </Typography>
            ) : (
              moduleGroups.map((group) => (
                <Box key={group.categoria} sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 750, color: 'text.secondary', mb: 0.5, pl: 1 }}>
                    {group.categoria}
                  </Typography>
                  {group.modulos.map((mod) => {
                    const modulo = mod.key;
                    const acciones = permisosMap[modulo] || [];
                    const selectedForModule = new Set((selectedPermissions?.[modulo] || []).map(String));
                    const allSelected = acciones.length > 0 && acciones.every((a) => selectedForModule.has(String(a)));
                    const someSelected = acciones.length > 0 && acciones.some((a) => selectedForModule.has(String(a)));

                    return (
                      <Accordion key={modulo} defaultExpanded={someSelected}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#fafafa' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected && !allSelected}
                              disabled={!acciones.length || permisosLoading}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSelectedPermissions((prev) => ({
                                  ...prev,
                                  [modulo]: checked ? acciones.map(String) : [],
                                }));
                              }}
                            />
                            <Typography sx={{ fontWeight: 650 }}>
                              {mod.label || formatModuloLabel(modulo)}
                            </Typography>
                            <Chip
                              label={`${selectedForModule.size}/${acciones.length}`}
                              size="small"
                              color={allSelected ? 'success' : someSelected ? 'warning' : 'default'}
                              sx={{ ml: 'auto' }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, backgroundColor: '#fff' }}>
                          {permisosLoading ? (
                            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                              <CircularProgress size={20} />
                            </Box>
                          ) : acciones.length === 0 ? (
                            <Typography sx={{ color: 'text.secondary', py: 1, px: 1 }}>
                              Sin acciones para este módulo
                            </Typography>
                          ) : (
                            <Grid container spacing={1.2}>
                              {acciones.map((action) => {
                                const checked = selectedForModule.has(String(action));
                                return (
                                  <Grid item xs={12} sm={6} md={4} key={`${modulo}-${action}`}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={checked}
                                          onChange={(e) => togglePermission(modulo, action, e.target.checked)}
                                        />
                                      }
                                      label={<Typography sx={{ textTransform: 'capitalize' }}>{action}</Typography>}
                                    />
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={closeRoleDialog} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveRole} sx={{ textTransform: 'none', fontWeight: 650 }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

